import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useOrgan } from "../../hooks/useOrgan";
import { organizationPrimaryColors } from "../../layouts/PermanentSidebarMUI";
import { renderTotalRow } from "../../utils/renderTotalRows";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";

// Função para formatar números (pode personalizar)
function ccyFormat(num) {
  return Number(num).toFixed(2);
}

// Função para calcular totais das colunas numéricas passadas
function calculateTotals(data, columns) {
  const totals = {};
  columns.forEach(({ key, numeric, totalCalc }) => {
    if (numeric) {
      totals[key] = data.reduce((acc, item) => {
        // Se houver uma função de cálculo customizada (ex: qtd * preco), usa ela.
        // Caso contrário, soma o valor normal da coluna (item[key]).
        const valueToSum =
          typeof totalCalc === "function" ? totalCalc(item) : item[key];
        return acc + (Number(valueToSum) || 0);
      }, 0);
    }
  });
  return totals;
}

function getNestedValue(obj, path) {
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
}

export default function TableDetails({
  data,
  columns,
  actions = [],
  rowColorConfig = null,
}) {
  const { selectedOrgan } = useOrgan();
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM;
  const totals = React.useMemo(
    () => calculateTotals(data, columns),
    [data, columns],
  );

  // estado do menu dropdown de ações
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [activeRow, setActiveRow] = React.useState(null);
  const open = Boolean(anchorEl);

  // Função para determinar as cores da linha
  const getRowColors = React.useMemo(() => {
    return (row) => {
      if (!rowColorConfig) {
        return { backgroundColor: "inherit", color: "inherit" };
      }

      const { colorMap, propertyKey } = rowColorConfig;
      const propertyValue = getNestedValue(row, propertyKey);

      if (colorMap && propertyValue !== undefined) {
        const colorConfig = colorMap[propertyValue];
        if (colorConfig) {
          return {
            backgroundColor: colorConfig.bgColor,
            color: colorConfig.color,
          };
        }
      }

      return { backgroundColor: "inherit", color: "inherit" };
    };
  }, [rowColorConfig]);

  const handleOpenMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setActiveRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveRow(null);
  };

  const handleActionClick = (action) => {
    handleCloseMenu();
    if (action?.onClick && activeRow) {
      action.onClick(activeRow);
    }
  };

  return (
    <TableContainer component={Paper}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          mb: 1,
        }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", color: "#858484" }}
          align="right">
          Total: {data.length}
        </Typography>
      </Box>
      <Table sx={{ minWidth: 700 }} aria-label="table details">
        <TableHead>
          <TableRow style={{ backgroundColor: primaryColor }}>
            {columns.map(({ key, label, align }) => (
              <TableCell
                sx={{ fontWeight: "bold", color: "#FFFFFF" }}
                key={key}
                align={align || "left"}>
                {label}
              </TableCell>
            ))}
            {actions.length > 0 && (
              <TableCell
                sx={{
                  fontWeight: "bold",
                  backgroundColor: primaryColor,
                  color: "#FFFFFF",
                }}>
                Ações
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, idx) => {
            const rowColors = getRowColors(row);

            return (
              <TableRow
                key={idx}
                sx={{
                  backgroundColor: rowColors.backgroundColor,
                  color: rowColors.color,
                }}>
                {columns.map(({ key, align, render, isMoney }) => (
                  <TableCell
                    key={key}
                    align={align || "left"}
                    sx={{
                      color: rowColors.color,
                    }}>
                    {render
                      ? render(row[key], row)
                      : isMoney
                        ? new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(row[key])
                        : row[key]}
                  </TableCell>
                ))}
                {actions.length > 0 && (
                  <TableCell
                    align="center"
                    sx={{
                      color: rowColors.color,
                    }}>
                    <IconButton
                      size="small"
                      sx={{ paddingX: 2 }}
                      onClick={(e) => handleOpenMenu(e, row)}>
                      <FontAwesomeIcon icon={faEllipsisVertical} />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            );
          })}

          {/* Linha total */}
          {renderTotalRow(columns, totals, ccyFormat)}
        </TableBody>
      </Table>

      {/* menu suspenso de ações */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}>
        {actions
          .filter((action) => {
            try {
              return action.show ? action.show(activeRow ?? {}) : true;
            } catch (e) {
              return false;
            }
          })
          .map((action, idx) => (
            <MenuItem
              key={idx}
              onClick={() => handleActionClick(action)}
              sx={{
                color:
                  action.buttonClass === "btn-danger"
                    ? "error.main"
                    : action.buttonClass === "btn-primary"
                      ? "primary.main"
                      : action.buttonClass === "btn-info"
                        ? "info.main"
                        : action.buttonClass === "btn-success"
                          ? "success.main"
                          : "text.secondary",
              }}>
              <FontAwesomeIcon icon={action.icon} style={{ marginRight: 8 }} />
              {action.title}
            </MenuItem>
          ))}
      </Menu>
    </TableContainer>
  );
}
